import { Box, type BoxProps } from '@mui/material';
import { type ReactNode } from 'react';
import { colors, spacing, densitySpacing } from '@/theme/tokens';
import { usePreferencesStore } from '@/stores';

interface AppLayoutProps {
  children: ReactNode;
  sidebar?: ReactNode;
  header?: ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

const maxWidthValues = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  full: '100%',
};

export function AppLayout({ children, sidebar, header, maxWidth = 'xl' }: AppLayoutProps) {
  const density = usePreferencesStore((s) => s.density);
  const ds = densitySpacing[density];
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        backgroundColor: colors.bg,
      }}
    >
      {header && (
        <Box
          component="header"
          sx={{
            backgroundColor: colors.bg,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          {header}
        </Box>
      )}
      <Box sx={{ display: 'flex', flex: 1 }}>
        {sidebar && (
          <Box
            component="aside"
            sx={{
              width: 240,
              flexShrink: 0,
              borderRight: `1px solid ${colors.border}`,
              backgroundColor: colors.surface,
            }}
          >
            {sidebar}
          </Box>
        )}
        <Box
          component="main"
          sx={{
            flex: 1,
            padding: { xs: spacing[3], sm: spacing[4], md: ds.pagePadding },
            maxWidth: maxWidthValues[maxWidth],
            margin: '0 auto',
            width: '100%',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

// Sub-component for page sections
interface AppPageSectionProps extends BoxProps {
  children: ReactNode;
}

export function AppPageSection({ children, sx, ...props }: AppPageSectionProps) {
  return (
    <Box
      sx={{
        marginBottom: spacing[8],
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
}
